import React, { useState, useCallback, useRef, useEffect } from "react";
import { Trash2 } from "lucide-react";
import { useCanvasContext } from "../WorkflowCanvasContext";

const COLORS = [
    { bg: "bg-yellow-100", border: "border-yellow-300", text: "text-yellow-900" },
    { bg: "bg-blue-100", border: "border-blue-300", text: "text-blue-900" },
    { bg: "bg-green-100", border: "border-green-300", text: "text-green-900" },
    { bg: "bg-pink-100", border: "border-pink-300", text: "text-pink-900" },
    { bg: "bg-purple-100", border: "border-purple-300", text: "text-purple-900" },
];

function StickyNoteNode({ id, data, selected }) {
    const { updateNote, deleteNote } = useCanvasContext();
    const [editing, setEditing] = useState(false);
    const textareaRef = useRef(null);
    const colorIdx = data.colorIndex || 0;
    const color = COLORS[colorIdx % COLORS.length];

    const handleDoubleClick = useCallback(() => {
        setEditing(true);
    }, []);

    useEffect(() => {
        if (editing && textareaRef.current) {
            textareaRef.current.focus();
            textareaRef.current.selectionStart = textareaRef.current.value.length;
        }
    }, [editing]);

    const handleBlur = useCallback(() => {
        setEditing(false);
        updateNote(id, { text: textareaRef.current?.value || "" });
    }, [id, updateNote]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === "Escape") { setEditing(false); updateNote(id, { text: textareaRef.current?.value || "" }); }
        e.stopPropagation();
    }, [id, updateNote]);

    const handleDelete = useCallback((e) => {
        e.stopPropagation();
        deleteNote(id);
    }, [id, deleteNote]);

    const cycleColor = useCallback((e) => {
        e.stopPropagation();
        updateNote(id, { colorIndex: (colorIdx + 1) % COLORS.length });
    }, [id, colorIdx, updateNote]);

    return (
        <div
            onDoubleClick={handleDoubleClick}
            className={`${color.bg} ${color.border} border rounded-md shadow-sm min-w-[160px] max-w-[240px] min-h-[80px] p-2 cursor-grab ${selected ? "ring-2 ring-blue-400" : ""}`}
        >
            {selected && (
                <div className="absolute -top-7 right-0 flex gap-1">
                    <button onClick={cycleColor} className="w-5 h-5 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center" title="Change color">
                        <div className={`w-3 h-3 rounded-full ${COLORS[(colorIdx + 1) % COLORS.length].bg}`} />
                    </button>
                    <button onClick={handleDelete} className="w-5 h-5 rounded bg-white border border-gray-200 shadow-sm flex items-center justify-center hover:bg-red-50" title="Delete note">
                        <Trash2 size={10} className="text-gray-500" />
                    </button>
                </div>
            )}
            {editing ? (
                <textarea
                    ref={textareaRef}
                    defaultValue={data.text || ""}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    className={`w-full h-full min-h-[60px] bg-transparent border-0 outline-none resize-none text-xs ${color.text} placeholder:text-gray-400 nodrag`}
                    placeholder="Type a note..."
                />
            ) : (
                <p className={`text-xs ${color.text} whitespace-pre-wrap break-words ${data.text ? "" : "text-gray-400 italic"}`}>
                    {data.text || "Double-click to edit"}
                </p>
            )}
        </div>
    );
}

export default React.memo(StickyNoteNode);
